import { useDisclosure } from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import { chooseProduct } from 'Containers/App/actions';
import {
  makeGetEnabledProducts,
  makeGetJvpHasPublicationWithFlag,
  makeSelectConfigField,
  makeSelectGlobalField,
} from 'Containers/App/selectors';
import { AlexProducts, EnabledProducts, JVPState } from 'Containers/App/types';
import { forceEnglishForBcPath } from 'Containers/App/utils';
import { setHouseholdExternalId } from 'Containers/CommercialRoutes/actions';
import { makeSelectProfileField, makeGetMedicareDestination } from 'Containers/ProfilePage/selectors';
import { ContentfulInfoBars } from 'ContentfulWrappers/ContentfulInfoBars';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import { JVPUserWithDependents } from 'Utils/apiTypes';
import { sendInsightsEvent } from 'Utils/insights';
import { shouldShowMedicareCallouts } from 'Utils/medicare';
import { INTRO_PATH, PROFILE_PATH } from 'Utils/urls';

import ClientDisclaimer from './ClientDisclaimer';
import { ConstructionPage } from './ConstructionPage';
import { Footer } from './Footer';
import { Footer2025 } from './Footer2025';
import { Hero } from './Hero';
import { Hero2025 } from './Hero2025';
// import { LibraryCarousel } from './LibraryCarousel'; // temporarily hidden, see line ~196
import { MedicareCallout } from './MedicareCallout';
import { MedicareCallout2025 } from './MedicareCallout2025';
import { OpenEnrollmentBar } from './OpenEnrollmentBar';
import { OpenEnrollmentBar2025 } from './OpenEnrollmentBar2025';
import { ProductSelectModal } from './ProductSelectModal';
import { MedicareDestination } from '../../constants';

interface UnifiedLandingPageStateProps {
  client: string;
  isUnderConstruction: boolean;
  products: EnabledProducts;
  bcUrlHash: string;
  isLoading: boolean;
  forceAlexID: boolean;
  clientSurveyIsLoading: boolean;
  jvp: JVPState;
  isAlexIdEnabled: boolean;
  error: Error | null;
  clientSurveyError: Error | null;
  jvpUserData: JVPUserWithDependents | null;
  isIntegratedUser: boolean;
  medicareDestination: MedicareDestination | null;
}

interface UnifiedLandingPageDispatchProps {
  editHouseholdExternalId: (externalId: string) => void;
  chooseProduct: (product: AlexProducts) => void;
}

export type UnifiedLandingPageProps = UnifiedLandingPageStateProps & UnifiedLandingPageDispatchProps;

export const UnifiedLandingPage = ({
  isUnderConstruction,
  client,
  products,
  isLoading,
  clientSurveyIsLoading,
  forceAlexID,
  jvp,
  isAlexIdEnabled,
  error,
  clientSurveyError,
  bcUrlHash,
  editHouseholdExternalId,
  chooseProduct,
  jvpUserData,
  isIntegratedUser,
  medicareDestination,
}: UnifiedLandingPageProps) => {
  const { isOpen, onOpen, onClose, getButtonProps, getDisclosureProps } = useDisclosure();
  const navigate = useNavigate();
  const { is_ulp_updates_enabled } = useFeatureFlagContext();

  /**
   * The following is a hacky way of working around an issue with Chakra UI:
   * aria-expanded is always set to true, even when the modal is not open
   * this fix can be removed when Chakra and React are updated to the next major version
   */
  const modalButtonAccessibilityProps = getButtonProps();
  modalButtonAccessibilityProps['aria-expanded'] = isOpen;

  const modalTriggerButtonRef = useRef();

  const [isNextClicked, setIsNextClicked] = useState(false);

  const isDevMode = localStorage.getItem('dev');
  const isOnGoNow = window.location.hostname.includes('gonow');
  const { retrieveContentfulData, locale, setLocale } = useTextContext();

  const hideOEBar = retrieveContentfulData<boolean>('unified_landing_page.hide_open_enrollment_bar', false);

  const ANIMATION_TIMING = !isDevMode ? 1250 : 0;

  const handleNext = () => {
    sendInsightsEvent(null, 'get_started');

    if (isOnGoNow) {
      goToNext(PROFILE_PATH);
    } else if (bcUrlHash && products.bc) {
      // User provided hash on URL, choose BC for them and skip modal
      chooseProduct('bc');
      forceEnglishForBcPath(locale, setLocale);
      goToNext(INTRO_PATH);
    } else if (products.bc && products.go) {
      onOpen();
    } else if (products.bc) {
      forceEnglishForBcPath(locale, setLocale);
      goToNext(INTRO_PATH);
    } else {
      goToNext(PROFILE_PATH);
    }
  };

  const goToNext = (path: string) => {
    setIsNextClicked(true);
    // Get external_id stored in local storage by SSO and fill it in policyholder if it exists and is a valid UUID
    // todo is this really specific to sso or no?
    const externalId = localStorage.getItem('external_id');
    if (externalId) editHouseholdExternalId(externalId);

    setTimeout(() => {
      navigate(path);
    }, ANIMATION_TIMING);
  };

  useEffect(() => {
    if (error) {
      throw error;
    }
    if (clientSurveyError) {
      throw clientSurveyError;
    }
  }, [error, clientSurveyError]);

  // Render Construction Page if flag is true
  if (isUnderConstruction) {
    return <ConstructionPage />;
  }

  const shouldUseModal = products.bc && products.go && !isOnGoNow;

  return (
    <>
      <PageLayout bg={is_ulp_updates_enabled ? 'transparent' : '--primary-white'} page="landing">
        <ContentfulInfoBars sectionKey="unified_landing_page" />

        <ClientDisclaimer />

        {is_ulp_updates_enabled ? (
          <Hero2025
            isNextClicked={isNextClicked}
            isLoading={isLoading || jvp.isLoading || clientSurveyIsLoading}
            handleNext={handleNext}
            isAlexIdEnabled={isAlexIdEnabled}
            forceAlexID={forceAlexID}
            getStartedButtonRef={shouldUseModal ? modalTriggerButtonRef : null}
            getStartedButtonAccessibilityProps={shouldUseModal ? modalButtonAccessibilityProps : {}}
          />
        ) : (
          <Hero
            isNextClicked={isNextClicked}
            isLoading={isLoading || jvp.isLoading || clientSurveyIsLoading}
            handleNext={handleNext}
            isAlexIdEnabled={isAlexIdEnabled}
            forceAlexID={forceAlexID}
            getStartedButtonRef={shouldUseModal ? modalTriggerButtonRef : null}
            getStartedButtonAccessibilityProps={shouldUseModal ? modalButtonAccessibilityProps : {}}
            userName={jvpUserData?.first_name || undefined}
          />
        )}

        {medicareDestination &&
          shouldShowMedicareCallouts(medicareDestination, products.medicare) &&
          (is_ulp_updates_enabled ? (
            <MedicareCallout2025
              client={client}
              isNextClicked={isNextClicked}
              medicareDestination={medicareDestination}
            />
          ) : (
            <MedicareCallout
              client={client}
              isNextClicked={isNextClicked}
              medicareDestination={medicareDestination}
            />
          ))}
        {/* {is_ulp_updates_enabled && <LibraryCarousel isNextClicked={isNextClicked} />} */}
      </PageLayout>

      <PageLayout bg={isNextClicked ? '--primary-white' : '--background-gray'} page="landing-footer">
        {is_ulp_updates_enabled ? (
          <Footer2025 isNextClicked={isNextClicked} />
        ) : (
          <Footer isNextClicked={isNextClicked} />
        )}
      </PageLayout>

      <ProductSelectModal
        isOpen={isOpen && !isNextClicked}
        onClose={onClose}
        chooseProduct={chooseProduct}
        handleNext={goToNext}
        modalTriggerButtonRef={modalTriggerButtonRef}
        modalAccessibilityProps={getDisclosureProps()}
        isIntegratedUser={isIntegratedUser}
      />

      {is_ulp_updates_enabled
        ? !hideOEBar && (
            <OpenEnrollmentBar2025
              isNextClicked={isNextClicked}
              openEnrollmentStartDate={jvp.upcoming?.publication.oe_start_date}
              openEnrollmentEndDate={jvp.upcoming?.publication.oe_end_date}
            />
          )
        : !hideOEBar && (
            <OpenEnrollmentBar
              isNextClicked={isNextClicked}
              openEnrollmentStartDate={jvp.upcoming?.publication.oe_start_date}
              openEnrollmentEndDate={jvp.upcoming?.publication.oe_end_date}
            />
          )}
    </>
  );
};

const mapStateToProps = createStructuredSelector<GlobalReducerState, UnifiedLandingPageStateProps>({
  client: makeSelectConfigField('client'),
  isUnderConstruction: makeSelectConfigField('is_under_construction'),
  forceAlexID: makeSelectConfigField('force_alex_id'),
  products: makeGetEnabledProducts(),
  bcUrlHash: makeSelectGlobalField('bcUrlHash'),
  isLoading: makeSelectGlobalField('isLoading'),
  jvp: makeSelectGlobalField('jvp'),
  clientSurveyIsLoading: makeSelectProfileField('clientSurveyIsLoading'),
  error: makeSelectGlobalField('error'),
  clientSurveyError: makeSelectProfileField('clientSurveyError'),
  isAlexIdEnabled: makeGetJvpHasPublicationWithFlag('alex_id_enabled'),
  jvpUserData: makeSelectGlobalField('jvpUser'),
  isIntegratedUser: makeSelectConfigField('is_integrated'),
  medicareDestination: makeGetMedicareDestination(),
});

export function mapDispatchToProps(dispatch): UnifiedLandingPageDispatchProps {
  return {
    editHouseholdExternalId: (externalId) => dispatch(setHouseholdExternalId(externalId)),
    chooseProduct: (product: AlexProducts) => dispatch(chooseProduct(product)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(UnifiedLandingPage);
